import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Alert,
  Snackbar
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale';
import { useDecks } from '../../hooks/useDecks';
import { useMatches } from '../../hooks/useMatches';
import { useFormats } from '../../hooks/useFormats';

const MatchRecorder: React.FC = () => {
  const [userDeckId, setUserDeckId] = useState('');
  const [opponentDeckId, setOpponentDeckId] = useState('');
  const [formatId, setFormatId] = useState('');
  const [result, setResult] = useState<'win' | 'loss'>('win');
  const [date, setDate] = useState<Date>(new Date());
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const { decks } = useDecks();
  const { formats } = useFormats();
  const { addMatch } = useMatches();

  // フォーマットに対応するデッキのみをフィルタリング
  const filteredDecks = formatId 
    ? decks.filter(deck => deck.formatId === formatId)
    : [];

  // フォーマット変更時にデッキ選択をリセット
  const handleFormatChange = (newFormatId: string) => {
    setFormatId(newFormatId);
    setUserDeckId('');
    setOpponentDeckId('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!formatId) {
      setError('フォーマットを選択してください');
      return;
    }
    
    if (!userDeckId) {
      setError('使用デッキを選択してください');
      return;
    }

    const opponentDeckName = opponentDeckId 
      ? filteredDecks.find(deck => deck.id === opponentDeckId)?.name || ''
      : '未登録デッキ';
    
    try {
      await addMatch(userDeckId, opponentDeckName, formatId, result, date);
      
      // 成功メッセージを作成
      const selectedFormat = formats.find(f => f.id === formatId)?.name || 'フォーマット不明';
      const selectedDeck = filteredDecks.find(d => d.id === userDeckId)?.name || 'デッキ不明';
      const resultText = result === 'win' ? '勝利' : '敗北';
      
      setSuccessMessage(
        `🏆 対戦記録を登録しました！\n` +
        `フォーマット: ${selectedFormat}\n` +
        `使用デッキ: ${selectedDeck}\n` +
        `結果: ${resultText}`
      );
      
      // フォーマット、使用デッキ、対戦相手デッキ、勝敗は保持し、日付のみリセット
      setDate(new Date());
      setSuccess(true);
    } catch (error) {
      setError('対戦記録の保存に失敗しました');
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          勝率記録
        </Typography>

        <Paper elevation={2} sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {success && (
              <Alert 
                severity="success" 
                onClose={() => setSuccess(false)}
                sx={{ 
                  '& .MuiAlert-message': {
                    whiteSpace: 'pre-line',
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }
                }}
              >
                {successMessage}
              </Alert>
            )}
            
            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <FormControl fullWidth>
              <InputLabel>フォーマット *</InputLabel>
              <Select
                value={formatId}
                label="フォーマット *"
                onChange={(e) => handleFormatChange(e.target.value)}
              >
                {formats.map((format) => (
                  <MenuItem key={format.id} value={format.id}>
                    {format.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!formatId}>
              <InputLabel>使用デッキ</InputLabel>
              <Select
                value={userDeckId}
                label="使用デッキ"
                onChange={(e) => setUserDeckId(e.target.value)}
              >
                {filteredDecks.map((deck) => (
                  <MenuItem key={deck.id} value={deck.id}>
                    {deck.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!formatId}>
              <InputLabel>対戦相手のデッキ（任意）</InputLabel>
              <Select
                value={opponentDeckId}
                label="対戦相手のデッキ（任意）"
                onChange={(e) => setOpponentDeckId(e.target.value)}
              >
                <MenuItem value="">
                  未選択
                </MenuItem>
                {filteredDecks.map((deck) => (
                  <MenuItem key={deck.id} value={deck.id}>
                    {deck.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                勝敗
              </Typography>
              <ToggleButtonGroup
                value={result}
                exclusive
                onChange={(_, newResult) => {
                  if (newResult !== null) {
                    setResult(newResult);
                  }
                }}
                fullWidth
              >
                <ToggleButton value="win" color="success">
                  勝ち
                </ToggleButton>
                <ToggleButton value="loss" color="error">
                  負け
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <DatePicker
              label="対戦日"
              value={date}
              onChange={(newDate) => setDate(newDate || new Date())}
              slotProps={{
                textField: {
                  fullWidth: true
                }
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={formats.length === 0 || filteredDecks.length === 0}
            >
              記録
            </Button>

            {decks.length === 0 && (
              <Alert severity="info">
                まずデッキを登録してください
              </Alert>
            )}

            {formats.length === 0 && (
              <Alert severity="info">
                まずフォーマットを登録してください
              </Alert>
            )}

            {formatId && filteredDecks.length === 0 && (
              <Alert severity="info">
                選択されたフォーマットに対応するデッキがありません。デッキ管理から該当フォーマットのデッキを登録してください。
              </Alert>
            )}
          </Box>
        </Paper>
        
        <Snackbar
          open={success}
          autoHideDuration={4000}
          onClose={() => setSuccess(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSuccess(false)} 
            severity="success" 
            variant="filled"
            sx={{ 
              minWidth: 300,
              '& .MuiAlert-message': {
                whiteSpace: 'pre-line',
                fontSize: '1rem'
              }
            }}
          >
            {successMessage}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default MatchRecorder;